/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { Observable, throwError, map } from 'rxjs';
import { HttpService, leerDeLaPestana } from '@nebula-shared/data-access';
import {
  ApiResponse,
  AppSummary,
  OpcionProducto,
  Role,
} from '../../../domain/src/lib/products.model';
import { PRODUCTS_ERRORS } from '../../../const/src/lib/products.constant';
import { ICON_MAPPING } from '../../../const/src/lib/icons-mapping.constants';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly http = inject(HttpService);
  private readonly MENU_ENDPOINT = '/simappe-admin/api/v1/menu';

  private readonly ICON_MAP: Record<string, string> = ICON_MAPPING;

  getModules(): Observable<AppSummary[]> {
    const userDataJson = leerDeLaPestana('user_data');

    if (!userDataJson) {
      return throwError(() => new Error(PRODUCTS_ERRORS.NO_USER_DATA));
    }

    const userData = JSON.parse(userDataJson);

    if (!userData.roles || !Array.isArray(userData.roles) || userData.roles.length === 0) {
      return throwError(() => new Error(PRODUCTS_ERRORS.NO_ROLES));
    }

    const roleIds: number[] = userData.roles.map((role: Role) => role.id);

    return this.http
      .post<ApiResponse<unknown>>(`${this.MENU_ENDPOINT}/read-by-roles-grouped`, roleIds)
      .pipe(
        map((response: ApiResponse<unknown>) => {
          const content = response?.response?.content || response?.content || response || [];

          if (!Array.isArray(content)) return [];

          const applications = this.extractApplicationsFromGrouped(content);

          return applications.map((app) => ({
            id: app.applicationId,
            // El codigo del producto (admin.product_template.code): con el se piden los
            // parametros de configuracion del producto activo (HT_ARQ_012).
            code: app.applicationCode,
            name: app.applicationName,
            description: app.applicationDescription,
            icon: this.convertToLucideIcon(app.applicationFavicon),
            route: app.moduleUrl || this.buildApplicationRoute(app),
            opciones: this.opcionesDelMenu(app.modules),
          }));
        }),
      );
  }

  /**
   * Opciones del menu de un producto, tal como las pinta el sidebar: grupo por grupo, sin repetir
   * la misma ruta cuando llega en varios roles.
   *
   * @param modulos modulos del producto en la respuesta del menu.
   * @returns las opciones del producto, en el orden del menu.
   */
  private opcionesDelMenu(modulos: any[] | undefined): OpcionProducto[] {
    const opciones = new Map<string, OpcionProducto>();
    (modulos ?? []).forEach((modulo) =>
      (modulo?.optionGroups ?? []).forEach((grupo: any) =>
        (grupo?.options ?? []).forEach((opcion: any) => {
          const ruta = opcion?.optionFullPath || opcion?.optionPath;
          if (!ruta || !opcion?.optionName || opciones.has(ruta)) return;
          opciones.set(ruta, {
            nombre: opcion.optionName,
            icono: this.convertToLucideIcon(opcion.optionAhrefIconClass),
            ruta,
          });
        }),
      ),
    );
    return Array.from(opciones.values());
  }

  private convertToLucideIcon(iconValue: string | undefined): string {
    if (!iconValue) {
      return 'File';
    }
    return iconValue;
  }

  private extractApplicationsFromGrouped(groupedData: any[]) {
    const applicationsMap = new Map<string, any>();

    groupedData.forEach((role) => {
      if (!role.applications || !Array.isArray(role.applications)) return;

      role.applications.forEach(
        (app: {
          applicationId: string;
          applicationCode: string;
          applicationName: string;
          applicationDescription: string;
          applicationFavicon: string;
          moduleUrl: string;
          modules: string;
        }) => {
          const appId = app.applicationId;

          if (!applicationsMap.has(appId)) {
            applicationsMap.set(appId, {
              applicationId: app.applicationId,
              applicationCode: app.applicationCode,
              applicationName: app.applicationName,
              applicationDescription: app.applicationDescription,
              applicationFavicon: app.applicationFavicon,
              moduleUrl: app.moduleUrl,
              modules: [],
            });
          }

          if (app.modules && Array.isArray(app.modules)) {
            const existingApp = applicationsMap.get(appId);
            existingApp.modules.push(...app.modules);
          }
        },
      );
    });

    return Array.from(applicationsMap.values());
  }

  private buildApplicationRoute(app: { modules: string | any[]; applicationName: string }): string {
    if (app.modules && app.modules.length > 0) {
      const firstModule = app.modules[0];
      if (firstModule.moduleUrl) {
        return firstModule.moduleUrl;
      }
    }

    const appName = app.applicationName?.toLowerCase().replace(/\s+/g, '-') || '';
    return `/${appName}`;
  }
}
